/**
 * AppSpinner Component
 *
 * Unified spinner component for consistent loading states across the app.
 * Uses smooth circular spinner instead of Ant Design's default dots.
 *
 * Features:
 * - Drop-in replacement for Ant Design Spin
 * - Smooth circular animation (LoadingOutlined)
 * - Supports all Ant Design Spin props
 * - Size variants: small, default, large
 *
 * Usage:
 *   import AppSpinner from '../shared/AppSpinner';
 *
 *   // Basic
 *   <AppSpinner />
 *
 *   // With size
 *   <AppSpinner size="large" />
 *
 *   // Wrapping content
 *   <AppSpinner spinning={isLoading}>
 *     <Content />
 *   </AppSpinner>
 *
 *   // With tip text
 *   <AppSpinner size="large" tip="Loading..." />
 */

import React from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

// Size mappings for the spinner icon
const ICON_SIZES = {
  small: 16,
  default: 24,
  large: 32,
};

/**
 * Get the loading indicator with appropriate size
 * @param {string} size - Spinner size (small, default, large)
 * @returns {React.ReactNode} LoadingOutlined icon with correct size
 */
const getIndicator = (size = 'default') => {
  const fontSize = ICON_SIZES[size] || ICON_SIZES.default;
  return <LoadingOutlined style={{ fontSize }} spin />;
};

/**
 * AppSpinner - Unified loading spinner
 * @param {Object} props - Component props (same as Ant Design Spin)
 */
const AppSpinner = ({
  size = 'default',
  spinning = true,
  tip,
  children,
  className,
  style,
  ...restProps
}) => {
  return (
    <Spin
      indicator={getIndicator(size)}
      size={size}
      spinning={spinning}
      tip={tip}
      className={className}
      style={style}
      {...restProps}
    >
      {children}
    </Spin>
  );
};

AppSpinner.propTypes = {
  /** Size of the spinner: 'small' | 'default' | 'large' */
  size: PropTypes.oneOf(['small', 'default', 'large']),
  /** Whether the spinner is active */
  spinning: PropTypes.bool,
  /** Loading text shown below spinner */
  tip: PropTypes.string,
  /** Content to wrap (spinner overlays this when spinning) */
  children: PropTypes.node,
  /** Additional CSS class */
  className: PropTypes.string,
  /** Inline styles */
  style: PropTypes.object,
};

AppSpinner.defaultProps = {
  size: 'default',
  spinning: true,
  tip: undefined,
  children: undefined,
  className: undefined,
  style: undefined,
};

export default AppSpinner;
